import { FC, Fragment, JSX } from "react";
import { AccessReportParametersModel } from "@app/access-reports/wizard";
import { AccessReportsWizardAccessCriteria } from "@app/access-reports/shared/accessReportsWizardAccessCriteria";
import { EmployeeGroup } from "@app/access-reports/shared/employeeGroup";
import { EmployeeGroupTypeEnum } from "@app/access-reports/shared/employeeGroupTypeEnum";
import { buildStandard } from "@app/access-reports/shared/utility";

interface AccessReportSummaryProps {
  accessReportParameters: AccessReportParametersModel;
  employeeGroup?: EmployeeGroup;
}

const AccessReportSummary: FC<AccessReportSummaryProps> = (props: AccessReportSummaryProps) => {
  const accessReportsGroupedProperties: string[] = [
    "productType",
    "product",
    "standard",
    "calculationMethod",
    "distanceMethod",
    "memberSet",
    "logo",
    "companyName",
    "requestorName",
    "internalClientId",
    "reportTitle",
    "createdBy",
    "emailTo",
    "splitBySpecialty",
    "splitEmployeeGroupByStandard",
    "reportPages",
  ];

  const buildReviewElement = (element: string): React.ReactNode => {
    let title: string;
    let content: JSX.Element;

    switch (element) {
      case "productType":
        if (!!props.accessReportParameters.productType) {
          title = "Product Type";
          content = (
            <p className="mb-0">
              {props.accessReportParameters.productType.displayName ?
                props.accessReportParameters.productType.displayName :
                props.accessReportParameters.productType.name
              }
            </p>
          );
        }
        break;
      case "product":
        if (!!props.accessReportParameters.product) {
          title = "Product";
          content = (
            <Fragment>
              <p className="mb-0">
                {props.accessReportParameters.product.displayName ?
                  props.accessReportParameters.product.displayName :
                  props.accessReportParameters.product.name
                }
              </p>
              {!props.accessReportParameters.product.skipAccessAnalysis && props.accessReportParameters.product.accessCriterion.map((accessCriteria, index) => (
                accessCriteria.selected && (
                  <p className="mb-0" key={index}>
                    {accessCriteria.name} @ {buildStandard(accessCriteria, props.accessReportParameters)}
                  </p>
                )
              ))}
            </Fragment>
          );
        }
        break;
      case "standard":
        if (!props.accessReportParameters.product.skipAccessAnalysis && !!props.accessReportParameters.standard) {
          title = "Access Standard";
          content = (
            <p className="mb-0">
              {props.accessReportParameters.standard.displayName ?
                props.accessReportParameters.standard.displayName :
                props.accessReportParameters.standard.name
              }
            </p>
          );
        }
        break;
      case "calculationMethod":
        if (!props.accessReportParameters.product.skipAccessAnalysis && !!props.accessReportParameters.calculationMethod) {
          title = "Calculation Method";
          content = (
            <p className="mb-0">
              {props.accessReportParameters.calculationMethod.displayName ?
                props.accessReportParameters.calculationMethod.displayName :
                props.accessReportParameters.calculationMethod.name
              }
            </p>
          );
        }
        break;
      case "distanceMethod":
        if (!props.accessReportParameters.product.skipAccessAnalysis && !!props.accessReportParameters.distanceMethod) {
          title = "Distance Method";
          content = (
            <p className="mb-0">
              {props.accessReportParameters.distanceMethod.displayName ?
                props.accessReportParameters.distanceMethod.displayName :
                props.accessReportParameters.distanceMethod.name
              }
            </p>
          );
        }
        break;
      case "memberSet":
        if (!props.accessReportParameters.product?.skipAccessAnalysis && (!!props.employeeGroup || !!props.accessReportParameters.product)) {
          let fileName: string;
          let employeeGroupType: string;
          let employeeGroupStatus: string;

          if (!!props.employeeGroup) {
            fileName = props.employeeGroup.fileName;
            employeeGroupType = props.employeeGroup.employeeGroupType === EmployeeGroupTypeEnum.Census ? EmployeeGroupTypeEnum.Census : EmployeeGroupTypeEnum.Listing;
            employeeGroupStatus = props.employeeGroup.employeeGroupStatus;
          } else {
            const accessCriteria: AccessReportsWizardAccessCriteria = props.accessReportParameters.product.accessCriterion.find(x => x.memberSet);
            if (!!accessCriteria) {
              fileName = accessCriteria.memberSet.fileName;
              employeeGroupType = accessCriteria.memberSet.isCensus ? EmployeeGroupTypeEnum.Census : EmployeeGroupTypeEnum.Listing;
              employeeGroupStatus = accessCriteria.memberSet.employeeGroupStatus;
            }
          }

          title = "Member Set";
          content = (
            <Fragment>
              {!!fileName &&
                <p className="mb-0">
                  {fileName}
                </p>
              }

              <p className="mb-0">
                {employeeGroupType && employeeGroupType.charAt(0).toUpperCase() + employeeGroupType.slice(1)}
              </p>

              {employeeGroupStatus &&
                <p className="mb-0">
                  {employeeGroupStatus.charAt(0).toUpperCase() + employeeGroupStatus.slice(1)}
                </p>
              }
            </Fragment>
          );
        }
        break;
      case "logo":
        if (!!props.accessReportParameters.logo) {
          title = "Logo";
          content = (
            <p className="mb-0">
              {props.accessReportParameters.logo.displayName ?
                props.accessReportParameters.logo.displayName :
                props.accessReportParameters.logo.name
              }
            </p>
          );
        }
        break;
      case "companyName":
        if (!!props.accessReportParameters.companyName) {
          title = "Company Name";
          content = (
            <p className="mb-0">
              {props.accessReportParameters.companyName}
            </p>
          );
        }
        break;
      case "requestorName":
        if (!!props.accessReportParameters.requestorName) {
          title = "Requestor Name";
          content = (
            <p className="mb-0">
              {props.accessReportParameters.requestorName}
            </p>
          );
        }
        break;
      case "internalClientId":
        if (!!props.accessReportParameters.internalClientId) {
          title = "Internal Id";
          content = (
            <p className="mb-0">
              {props.accessReportParameters.internalClientId}
            </p>
          );
        }
        break;
      case "reportTitle":
        if (!!props.accessReportParameters.reportTitleOne || !!props.accessReportParameters.reportTitleTwo || !!props.accessReportParameters.reportTitleThree) {
          title = "Report Title";
          content = (
            <Fragment>
              {!!props.accessReportParameters.reportTitleOne && <p className="mb-0">{props.accessReportParameters.reportTitleOne}</p>}
              {!!props.accessReportParameters.reportTitleTwo && <p className="mb-0">{props.accessReportParameters.reportTitleTwo}</p>}
              {!!props.accessReportParameters.reportTitleThree && <p className="mb-0">{props.accessReportParameters.reportTitleThree}</p>}
            </Fragment>
          );
        }
        break;
      case "createdBy":
        if (!!props.accessReportParameters.createdByOne || !!props.accessReportParameters.createdByTwo || !!props.accessReportParameters.createdByThree) {
          title = "Created By";
          content = (
            <Fragment>
              {!!props.accessReportParameters.createdByOne && <p className="mb-0">{props.accessReportParameters.createdByOne}</p>}
              {!!props.accessReportParameters.createdByTwo && <p className="mb-0">{props.accessReportParameters.createdByTwo}</p>}
              {!!props.accessReportParameters.createdByThree && <p className="mb-0">{props.accessReportParameters.createdByThree}</p>}
            </Fragment>
          );
        }
        break;
      case "emailTo":
        if (!!props.accessReportParameters.accessSharedOutput?.to) {
          title = "Email To";
          content = (
            <p className="mb-0">
              {props.accessReportParameters.accessSharedOutput.to}
            </p>
          );
        }
        break;
      case "splitBySpecialty":
        if (props.accessReportParameters.splitPagesBySpecialty) {
          title = "Split By Specialty";
          content = (
            <p className="mb-0">
              true
            </p>
          );
        }
        break;
      case "splitEmployeeGroupByStandard":
        if (props.accessReportParameters.splitEmployeeGroupByStandard) {
          title = "Split Employee Group By Standard";
          content = (
            <p className="mb-0">
              true
            </p>
          );
        }
        break;
      case "reportPages":
        const summaryPages = props.accessReportParameters.accessReportsWizardSummaryPages.filter(x => x.accessSummary);
        const detailsPages = props.accessReportParameters.accessReportsWizardDetailsPages.filter(x => x.accessDetail);
        const employeeCountPages = props.accessReportParameters.employeeCountPages.filter(x => x.employeeCount);
        const accessOverviewPages = props.accessReportParameters.accessOverviewPages.filter(x => x.accessOverview);
        const accessListingPages = props.accessReportParameters.accessListingPages.filter(x => x.accessListing);
        const radiusSearchPages = props.accessReportParameters.accessRadiusSearchPages.filter(x => x.accessRadiusSearch);
        const providerCountPages = props.accessReportParameters.providerCountPages.filter(x => x.providerCount);

        if (props.accessReportParameters.coverPage ||
          props.accessReportParameters.contentsPage ||
          props.accessReportParameters.separatorPage ||
          summaryPages.length > 0 ||
          detailsPages.length > 0 ||
          employeeCountPages.length > 0 ||
          accessOverviewPages.length > 0 ||
          accessListingPages.length > 0 ||
          radiusSearchPages.length > 0 ||
          providerCountPages.length > 0
        ) {
          title = "Report Pages";
          content = (
            <Fragment>
              {props.accessReportParameters.coverPage &&
                <p className="mb-0">
                  Cover Page
                </p>
              }
              {props.accessReportParameters.contentsPage &&
                <p className="mb-0">
                  Contents Page
                </p>
              }
              {props.accessReportParameters.separatorPage &&
                <p className="mb-0">
                  Separator Page
                </p>
              }
              {summaryPages.map((summaryPage, index) => (
                <p className="mb-0" key={index}>
                  Access Summary Page
                  {summaryPage.accessFilter && (
                    " with access filter of " + (summaryPage.accessFilter.displayName ? summaryPage.accessFilter.displayName : summaryPage.accessFilter.name)
                  )}
                  {summaryPage.summaryType && (
                    ((summaryPage.summarizeBy && summaryPage.summaryType.id === 1) ? ", " : " and ") + (summaryPage.summaryType.displayName ? summaryPage.summaryType.displayName : summaryPage.summaryType.name) + " summary type"
                  )}
                  {(summaryPage.summarizeBy && summaryPage.summaryType.id === 1) && (
                    ", and summarized by " + (summaryPage.summarizeBy.displayName ? summaryPage.summarizeBy.displayName : summaryPage.summarizeBy.name)
                  )}
                </p>
              ))}
              {detailsPages.map((detailsPage, index) => (
                <p className="mb-0" key={index}>
                  Access Details Page
                  {detailsPage.accessFilter && (" with access filter of " + (detailsPage.accessFilter.displayName ? detailsPage.accessFilter.displayName : detailsPage.accessFilter.name))}
                  {detailsPage.summarizeBy && (" and summarized by " + (detailsPage.summarizeBy.displayName ? detailsPage.summarizeBy.displayName : detailsPage.summarizeBy.name))}
                </p>
              ))}
              {employeeCountPages.map((employeeCountPage, index) => (
                <p className="mb-0" key={index}>
                  Employee Count Page
                  {(employeeCountPage.hasServiceArea && employeeCountPage.filteredWithinServiceArea) && (
                    " filtered " + (employeeCountPage.filteredWithinServiceArea.displayName ? employeeCountPage.filteredWithinServiceArea.displayName : employeeCountPage.filteredWithinServiceArea.name) + " service area and"
                  )}
                  {employeeCountPage.summarizeBy && (
                    " summarized by " + (employeeCountPage.summarizeBy.displayName ? employeeCountPage.summarizeBy.displayName : employeeCountPage.summarizeBy.name)
                  )}
                </p>
              ))}
              {accessOverviewPages.map((overviewPage, index) => (
                <p className="mb-0" key={index}>
                  Access Overview Page
                  {overviewPage.accessFilter && (" with access filter of " + (overviewPage.accessFilter.displayName ? overviewPage.accessFilter.displayName : overviewPage.accessFilter.name))}
                </p>
              ))}
              {accessListingPages.map((accessListingPage, index) => (
                <p className="mb-0" key={index}>
                  Access Listing Page
                  {accessListingPage.accessFilter && (" with access filter of " + (accessListingPage.accessFilter.displayName ? accessListingPage.accessFilter.displayName : accessListingPage.accessFilter.name))},
                  {!!accessListingPage.minCount && (` min count: ${accessListingPage.minCount},`)}
                  {!!accessListingPage.maxCount && (` max count: ${accessListingPage.maxCount},`)}
                  {!!accessListingPage.maxMiles && (` max miles: ${accessListingPage.maxMiles},`)}
                  {!!accessListingPage.maxMinutes && (` and max minutes: ${accessListingPage.maxMinutes},`)}
                </p>
              ))}
              {radiusSearchPages.map((radiusSearchPage, index) => (
                <p className="mb-0" key={index}>
                  Access Radius Search Page
                  {!!radiusSearchPage.accessFilter && ` with access filter of ${(radiusSearchPage.accessFilter.displayName ? radiusSearchPage.accessFilter.displayName : radiusSearchPage.accessFilter.name)},`}
                  {!!radiusSearchPage.searchByType && ` searching by ${(radiusSearchPage.searchByType.displayName ? radiusSearchPage.searchByType.displayName : radiusSearchPage.searchByType.name)},`}
                  {!radiusSearchPage.useExclusiveBands && " not"} using exclusive bands,
                  {!!radiusSearchPage.radiiOne && ` radii one: ${radiusSearchPage.radiiOne}`}
                  {!!radiusSearchPage.radiiTwo && `, radii two: ${radiusSearchPage.radiiTwo}`}
                  {!!radiusSearchPage.radiiThree && `, radii three: ${radiusSearchPage.radiiThree}`}
                  {!!radiusSearchPage.radiiFour && `, radii four: ${radiusSearchPage.radiiFour}`}
                  {!!radiusSearchPage.radiiFive && `, radii five: ${radiusSearchPage.radiiFive}`}
                </p>
              ))}
              {providerCountPages.map((providerCountPage, index) => (
                <p className="mb-0" key={index}>
                  Provider Count Page
                  {(providerCountPage.hasServiceArea && providerCountPage.filteredWithinServiceArea) && (
                    " filtered " + (providerCountPage.filteredWithinServiceArea.displayName ? providerCountPage.filteredWithinServiceArea.displayName : providerCountPage.filteredWithinServiceArea.name) + " service area and"
                  )}
                  {providerCountPage.summarizeBy && (
                    " summarized by " + (providerCountPage.summarizeBy.displayName ? providerCountPage.summarizeBy.displayName : providerCountPage.summarizeBy.name)
                  )}
                </p>
              ))}
            </Fragment>
          );
        }
        break;
      default:
        break;
    }


    if (!!title && !!content) {
      return (
        <div className="row mb-3">
          <div className="col-2">
            <b>{title}</b>
          </div>
          <div className="col-10">
            {content}
          </div>
        </div>
      );
    }
  }

  return (
    <div id="access-reports-review" className="copy-text-default">
      {accessReportsGroupedProperties.map((ar: string, index: number) => (
        <Fragment key={index}>
          {buildReviewElement(ar)}
        </Fragment>
      ))}
    </div>
  );
}

export { AccessReportSummary };
